import { AdminApiError, adminApiUrl } from './adminApi';

/**
 * /api/admin/agent-prompts — the system prompt overrides (kind 'system'), moved
 * from the product's Settings -> Agent to this console. Superadmin only: the
 * API refuses every other console role. A prompt is keyed by (kind, key) and
 * applies to every model and every agent.
 */
export type AgentPromptKind = 'system' | 'window' | 'config';

export interface AgentPromptRow {
  id: string;
  kind: AgentPromptKind;
  key: string;
  title: string;
  content: string;
  enabled: boolean;
  date_updated: string;
}

/** One slot the gateway reads (services/agent-gateway/app/api/prompt_slots.py is
 * the list). `format: 'json'` means the value is parsed as a JSON object. */
export interface PromptSlot {
  key: string;
  label: string;
  hint: string;
  /** 'agent' | 'series' today; an unknown group is shown under its raw name. */
  group: string;
  format: 'text' | 'json';
  default: string;
}

export interface PromptSlots {
  slots: PromptSlot[];
}

const BASE = '/api/admin/agent-prompts';

async function send<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const res = await fetch(adminApiUrl(path), {
    credentials: 'include',
    ...init,
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new AdminApiError(body.error ?? `${fallback}: ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

export function listPrompts(): Promise<AgentPromptRow[]> {
  return send(BASE, {}, 'prompts list failed');
}

/** POST is an upsert on (kind, key). No `model` field: api-py answers 400 to one. */
export function upsertPrompt(body: {
  kind: AgentPromptKind;
  key: string;
  title?: string;
  content?: string;
  enabled?: boolean;
}): Promise<AgentPromptRow> {
  return send(BASE, { method: 'POST', body: JSON.stringify(body) }, 'prompt save failed');
}

export function patchPrompt(
  id: string,
  body: Partial<Pick<AgentPromptRow, 'title' | 'content' | 'enabled' | 'key'>>,
): Promise<AgentPromptRow> {
  return send(
    `${BASE}/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    'prompt update failed',
  );
}

export function deletePrompt(id: string): Promise<{ ok: boolean }> {
  return send(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' }, 'prompt delete failed');
}

/** The slots with their built-in prompts, fetched by api-py from agent-gateway.
 * 502 when the gateway cannot answer. A body without a `slots` list (an old
 * gateway during a rollout) is an error, not an empty list. */
export async function fetchPromptDefaults(): Promise<PromptSlots> {
  const body = await send<Partial<PromptSlots>>(`${BASE}/defaults`, {}, 'prompt defaults failed');
  if (!Array.isArray(body.slots)) {
    throw new Error('The gateway did not return the prompt slot list (is it mid-rollout?).');
  }
  return { slots: body.slots };
}
